import { useLazyQuery, useQuery } from '@apollo/client';
import { HouseDivisionType } from '@dokucoach/common-types';
import { Box } from '@material-ui/core';
import { AdaptSelect } from 'components/AdaptField';
import CircularLoading from 'components/CircularLoading';
import { DropdownOption } from 'components/CustomDropdown';
import DcDataGrid from 'components/DevExtremeDataGrid';
import FilterCheckBox, { FilterHelperValues, FilterValues } from 'components/FilterCheckBox';
import SectionWrapper from 'components/SectionWrapper';
import { useContextSnackbar } from 'components/Snackbar/context';
import DataGrid, { Column } from 'devextreme-react/data-grid';
import { Field, Form, Formik, FormikProps } from 'formik';
import { DataGridRef, useDataGridExporter } from 'hooks/useDataGridExporter';
import React, { LegacyRef, Ref, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GET_HOUSE_DIVISIONS, GET_HOUSE_DIVISIONS_BY_TYPE } from 'services/houseDivisions';
import { getDefaultGroup } from 'utils/groups';
import ListHeader from '../common/ListHeader';

interface HouseDivision {
  id: string;
  type: HouseDivisionType;
  name: string;
  parentId: string | null;
  residentId: string | null;
}

interface HouseDivisionFreeBed {
  id: string;
  bed: string;
  room?: string;
  station?: string;
  living_area?: string;
}

interface Filters {
  livingArea: string;
}

const ALL = 'ALL';
const initialValues: Filters = {
  livingArea: ALL,
};

const freeBedsFilter: FilterValues[] = [
  {
    id: HouseDivisionType.BED,
    label: 'lists_freeBeds_bed',
    value: false,
    disabled: false,
  },
  {
    id: HouseDivisionType.ROOM,
    label: 'lists_freeBeds_room',
    value: false,
    disabled: false,
  },
  {
    id: HouseDivisionType.STATION,
    label: 'lists_freeBeds_station',
    value: false,
    disabled: false,
  },
  {
    id: HouseDivisionType.LIVING_AREA,
    label: 'lists_freeBeds_livingArea',
    value: false,
    disabled: false,
  },
];

const freeBedsFilterHelper: FilterHelperValues[] = [
  {
    checkBox: {
      id: 'check_all',
      label: 'lists_all',
      value: true,
      disabled: false,
    },
    group: [
      HouseDivisionType.BED,
      HouseDivisionType.ROOM,
      HouseDivisionType.STATION,
      HouseDivisionType.LIVING_AREA,
    ],
    default: true,
  },
];

const FreeBedsList = () => {
  const { t } = useTranslation();
  const [dataSource, setDataSource] = useState<HouseDivisionFreeBed[]>([]);
  const initialOption: DropdownOption = useMemo(() => ({ label: t('all'), value: ALL }), [t]);
  const [livingAreaOptions, setLivingAreaOptions] = useState<DropdownOption[]>([initialOption]);
  const formRef = useRef<FormikProps<Filters>>();
  const tableRef = useRef<DataGrid<unknown, string>>();
  const { showApiError } = useContextSnackbar();
  const { exportExcel, exportPdf } = useDataGridExporter();
  const [getList, { data, error, loading }] = useLazyQuery(GET_HOUSE_DIVISIONS, {
    fetchPolicy: 'no-cache',
  });
  const [filterResult, setFilterResult] = useState<Array<string>>(
    getDefaultGroup(freeBedsFilterHelper),
  );

  const {
    data: livingAreasData,
    error: livingAreasError,
    loading: livingAreasLoading,
  } = useQuery(GET_HOUSE_DIVISIONS_BY_TYPE, {
    variables: { type: HouseDivisionType.LIVING_AREA },
  });

  const updateDataSource = (values?: Filters) => {
    if (!data) return;

    values = values ?? formRef.current?.values;

    const houseDivisions: HouseDivision[] = data.houseDivisions;
    const houseDivisionById: Record<string, HouseDivision> = {};
    for (const houseDivision of houseDivisions) {
      houseDivisionById[houseDivision.id] = houseDivision;
    }

    const freeBeds: HouseDivisionFreeBed[] = [];
    for (const houseDivision of houseDivisions) {
      // only free beds
      if (houseDivision.type !== HouseDivisionType.BED || houseDivision.residentId !== null)
        continue;

      let station: string | undefined = undefined;
      let room: string | undefined = undefined;
      let livingArea: string | undefined = undefined;
      let livingAreaId: string | undefined = undefined;
      let currentLivingArea = houseDivision;
      while (currentLivingArea) {
        if (currentLivingArea.type === HouseDivisionType.STATION) {
          station = currentLivingArea.name;
        }
        if (currentLivingArea.type === HouseDivisionType.ROOM) {
          room = currentLivingArea.name;
        }
        if (currentLivingArea.parentId === null) {
          livingArea = currentLivingArea.name;
          livingAreaId = currentLivingArea.id;
          break;
        } else {
          currentLivingArea = houseDivisionById[currentLivingArea.parentId];
        }
      }

      const filteredLivingArea = formRef.current?.values.livingArea;
      if (livingAreaId && (filteredLivingArea === 'ALL' || filteredLivingArea === livingAreaId)) {
        freeBeds.push({
          id: houseDivision.id,
          bed: houseDivision.name,
          room,
          station,
          living_area: livingArea,
        });
      }
    }
    setDataSource(freeBeds);
  };

  useEffect(() => {
    if (error) {
      showApiError(error);
      return;
    }
    if (data) {
      updateDataSource();
    }
  }, [data, error, loading]);

  useEffect(() => {
    if (livingAreasError) {
      showApiError(livingAreasError);
      return;
    }
    if (livingAreasData) {
      setLivingAreaOptions([
        initialOption,
        ...livingAreasData.houseDivisions.map((i: { name: string; id: string }) => ({
          label: i.name,
          value: i.id,
        })),
      ]);
    }
  }, [livingAreasError, livingAreasData]);

  const onRefresh = () => {
    getList();

    // BUG: getList doesn't re-render after first fetch, and so updateDataSource needs to be
    // manually run.
    updateDataSource(formRef.current?.values);
  };

  const handleExport = () => {
    exportExcel({
      tableRef: tableRef as unknown as LegacyRef<DataGrid<unknown, string>>,
      fileName: t('lists_freeBeds'),
      sheetName: t('lists_freeBeds'),
      addDateTimeToFileName: true,
    });
  };

  const handlePDf = () => {
    if (!tableRef?.current?.instance) return;

    exportPdf({
      tableRef: tableRef as unknown as LegacyRef<DataGrid<unknown, string>>,
      fileName: t('lists_freeBeds'),
      pageTitle: t('lists_freeBeds'),
      orientation: 'landscape',
      addDateTimeToFileName: true,
      action: 'open',
    });
  };

  const onFilterChange = async (filter: Array<string>) => {
    if (!dataSource) {
      setFilterResult(filter);
      return;
    }
    setFilterResult(filter);
  };

  return (
    <>
      <ListHeader
        title={t('lists_freeBeds')}
        handleClickMainButton={onRefresh}
        handlePrint={handlePDf}
        handleExport={handleExport}
        disableMainButton={filterResult.length === 0}
        disableExport={filterResult.length === 0}
        disablePrint={filterResult.length === 0}
      >
        <Formik
          onSubmit={onRefresh}
          initialValues={initialValues}
          innerRef={formRef as unknown as Ref<FormikProps<Filters>>}
        >
          <Form>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Field
                component={AdaptSelect}
                name="livingArea"
                label={t('txt_living_area')}
                editable={true}
                dropdownList={livingAreaOptions}
                wrapper={{ py: 0, width: '200px', mr: '.5rem' }}
                style={{ maxWidth: '200px' }}
              />
            </Box>
          </Form>
        </Formik>
        <FilterCheckBox
          items={freeBedsFilter}
          helperItems={freeBedsFilterHelper}
          onFilterChange={onFilterChange}
        />
      </ListHeader>
      {livingAreasLoading || loading ? (
        <CircularLoading />
      ) : filterResult.length !== 0 ? (
        <SectionWrapper positionRelative={true}>
          <DcDataGrid
            dataSource={dataSource}
            rowKeyProperty="id"
            showRowNumbers={true}
            tableRef={tableRef as unknown as DataGridRef}
          >
            <Column
              dataField={HouseDivisionType.BED.toLowerCase()}
              caption={t('lists_freeBeds_bed').toUpperCase()}
              visible={filterResult.includes(HouseDivisionType.BED)}
            />
            <Column
              dataField={HouseDivisionType.ROOM.toLowerCase()}
              caption={t('lists_freeBeds_room').toUpperCase()}
              visible={filterResult.includes(HouseDivisionType.ROOM)}
            />
            <Column
              dataField={HouseDivisionType.STATION.toLowerCase()}
              caption={t('lists_freeBeds_station').toUpperCase()}
              visible={filterResult.includes(HouseDivisionType.STATION)}
            />
            <Column
              dataField={HouseDivisionType.LIVING_AREA.toLowerCase()}
              caption={t('lists_freeBeds_livingArea').toUpperCase()}
              visible={filterResult.includes(HouseDivisionType.LIVING_AREA)}
            />
          </DcDataGrid>
        </SectionWrapper>
      ) : null}
    </>
  );
};

export default FreeBedsList;
